import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, EyeOff, Hourglass, Link2Off, Lock, Mail, MessageSquareText, PenLine, PenTool, RefreshCw, Trash2,
  WifiOff, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MailWerkstatt } from '../../components/Zusammenarbeit/MailWerkstatt';
import { Avatar } from '../../components/Planung/Avatar';
import { EmpfaengerChip, EventChip, StandBadge, VersandChip } from '../../components/Planung/PlanungsChips';
import {
  alsTag, anredeName, darfUeberLinkSchreiben, datumKurz, istLinkAnmeldeFehler, linkAnmeldungFehlt, linkMail, linkSenden, linkVersion,
  linkZurueckziehen, tageText, type PlanungsDetail, type WerkstattAdapter,
} from '../../lib/planung';
import { AdminAnsichtBanner, AnmeldeBanner, AnmeldeSeite } from './Anmeldung';
import { nurLesenText, useAnmeldeKennung } from './linkAnmeldung';
import './FeedbackMailAnsicht.css';

const TOKEN_RE = /^[0-9a-f]{64}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ERKLAERT_KEY = 'fb-erklaert';

function erklaertGemerkt(): boolean {
  try { return localStorage.getItem(ERKLAERT_KEY) === '1'; } catch { return false; }
}
function erklaertMerken() {
  try { localStorage.setItem(ERKLAERT_KEY, '1'); } catch { /* ohne Speicher erscheint die Erklärung beim nächsten Mal wieder */ }
}

/**
 * Persönliche Links gehören in keine Suchmaschine und in keinen Referrer.
 * Setzt die Seite drumherum (FeedbackSeite) die Angaben schon, bleibt es
 * dabei; sonst kommen eigene meta-Elemente dazu und gehen beim Verlassen
 * wieder. Erst nach dem Zug, damit die Effekte der Eltern schon gelaufen
 * sind — so übernimmt niemand die Elemente des anderen.
 */
const SCHUTZ: [string, string][] = [['robots', 'noindex, nofollow'], ['referrer', 'no-referrer']];

function useLinkMeta() {
  useEffect(() => {
    const eigene: HTMLMetaElement[] = [];
    const t = window.setTimeout(() => {
      for (const [name, inhalt] of SCHUTZ) {
        const da = Array.from(document.head.querySelectorAll<HTMLMetaElement>(`meta[name="${name}"]`)).some((m) => m.content === inhalt);
        if (da) continue;
        const el = document.createElement('meta');
        el.name = name;
        el.content = inhalt;
        document.head.appendChild(el);
        eigene.push(el);
      }
    }, 0);
    return () => {
      window.clearTimeout(t);
      eigene.forEach((el) => el.remove());
    };
  }, []);
}

type Props = { token: string; mailId: string; onZurueck: () => void };

/**
 * Eine Mail über den persönlichen Link (/feedback/<token>/<mailId>): Kopf
 * im Markenverlauf, Anmelde-Hinweis, die Bitte des Teams, eine kurze
 * Erklärung und die Werkstatt — kommentieren, markieren, Text ändern
 * (als Vorschlag) oder „Passt so“. Ansehen und schreiben nur, wenn man mit
 * dem Google-Konto der Person angemeldet ist (anmeldung.status 'ok') —
 * sonst liefert die Datenbank keine Mail und die Seite zeigt nur die
 * Anmeldung. Admins sehen die Mail wie die Person, aber nur zum Lesen
 * (Admin-Ansicht mit „In der Werkstatt öffnen“). Token und Anmeldung prüft
 * die Datenbank bei jeder Abfrage.
 */
export function FeedbackMailAnsicht({ token, mailId, onZurueck }: Props) {
  useLinkMeta();
  const qc = useQueryClient();
  const formatOk = TOKEN_RE.test(token) && UUID_RE.test(mailId);
  // Nach An- oder Abmelden neu laden: Die Anmeldung gehört zum Abfrage-Schlüssel.
  const kennung = useAnmeldeKennung();
  const abfrage = useQuery({
    queryKey: ['link-mail', token, mailId, kennung ?? 'niemand'],
    queryFn: () => linkMail(token, mailId),
    enabled: formatOk && kennung !== undefined,
    retry: 1,
  });
  const { refetch } = abfrage;
  const r = abfrage.data;
  const daten = r && r.ok ? r : null;
  const [erklaert, setErklaert] = useState(erklaertGemerkt);

  // Die eigene Antwort steht in der Anfrage — fehlt sie an der Mail, von dort übernehmen.
  const mail = useMemo<PlanungsDetail | null>(() => {
    if (!daten) return null;
    const m = daten.mail;
    if (m.meine_anfrage || !daten.anfrage) return m;
    const a = daten.anfrage;
    return { ...m, meine_anfrage: { antwort: a.antwort, gesehen_am: a.gesehen_am, nachricht: a.nachricht, angefragt_von_name: a.angefragt_von_name } };
  }, [daten]);

  const personName = daten?.person.name ?? '';
  const anmeldung = daten?.anmeldung ?? null;
  // Admin liest den Link einer anderen Person mit (Migration 0052): nur ansehen, nie schreiben.
  const admin = daten?.admin_ansicht === true;
  const darfSchreiben = !admin && darfUeberLinkSchreiben(anmeldung);
  const leseHinweis = anmeldung ? nurLesenText(anmeldung, personName) : '';
  const adapter = useMemo<WerkstattAdapter | null>(() => {
    if (!personName) return null;
    // Auch die Übersicht (Zähler, eigene Antwort) ist danach veraltet.
    const neuLaden = async () => {
      await Promise.all([refetch(), qc.invalidateQueries({ queryKey: ['link-uebersicht', token] })]);
    };
    const basis: WerkstattAdapter = {
      art: 'link',
      ichName: personName,
      darfEntscheiden: false,
      version: (nr) => linkVersion(token, mailId, nr),
      neuLaden,
    };
    // Admin-Ansicht oder nicht (richtig) angemeldet: nur lesen — kein Feld, kein Stift, kein „Passt so“, kein Zurückziehen.
    if (!darfSchreiben) return { ...basis, nurLesenHinweis: leseHinweis };
    // Abgemeldet oder Sitzung abgelaufen, während die Seite offen war: neu laden, dann zeigt der Hinweis oben, was fehlt.
    const pruefen = async <T,>(f: () => Promise<T>): Promise<T> => {
      try {
        return await f();
      } catch (e) {
        if (istLinkAnmeldeFehler(e)) void refetch();
        throw e;
      }
    };
    return {
      ...basis,
      senden: (d) => pruefen(() => linkSenden(token, mailId, d)),
      zurueckziehen: (id) => pruefen(() => linkZurueckziehen(token, id)),
    };
  }, [personName, darfSchreiben, leseHinweis, token, mailId, refetch, qc]);

  const betreff = mail ? (mail.betreff?.trim() || mail.name) : '';
  useEffect(() => {
    const vorher = document.title;
    const vorn = admin ? 'Admin-Ansicht: ' : '';
    document.title = betreff ? `${vorn}${betreff} · Abstimmung · Randori Mail` : `${vorn}Abstimmung · Randori Mail`;
    return () => { document.title = vorher; };
  }, [betreff, admin]);

  // Beim Wechsel auf eine andere Mail oben anfangen.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [mailId]);

  function erklaerungSchliessen() {
    setErklaert(true);
    erklaertMerken();
  }

  // ── Ohne passende Anmeldung: nur die Anmeldeseite, keine Mail ────────────
  const fehlt = linkAnmeldungFehlt(r);
  if (formatOk && fehlt) {
    const name = r && !r.ok && r.person ? anredeName(r.person.name) : null;
    return <AnmeldeSeite anmeldung={fehlt} name={name} />;
  }

  // ── Ohne Mail: ungültig, nicht (mehr) freigegeben, offline, lädt ──────────
  if (!formatOk || (r && !r.ok)) {
    const keineMail = formatOk && r && !r.ok && r.fehler === 'keine_mail';
    return (
      <Rahmen onZurueck={onZurueck}>
        <Hinweis
          icon={keineMail ? EyeOff : Link2Off}
          titel={keineMail ? 'Diese Mail ist nicht (mehr) für Dich freigegeben' : 'Dieser Link ist nicht (mehr) gültig'}
          text={keineMail
            ? 'Vielleicht ist sie schon verschickt oder wurde zurückgezogen. Deine anderen Mails findest Du in der Übersicht.'
            : 'Bitte frag bei der Person nach, die ihn Dir geschickt hat — sie kann Dir einen neuen schicken.'}
          onZurueck={onZurueck}
        />
      </Rahmen>
    );
  }

  if (!daten || !mail || !adapter) {
    if (abfrage.isError) {
      return (
        <Rahmen onZurueck={onZurueck}>
          <Hinweis
            icon={WifiOff}
            titel="Gerade keine Verbindung"
            text="Die Mail ließ sich nicht laden. Prüf Deine Internetverbindung und versuch es gleich noch einmal."
            onZurueck={onZurueck}
            extra={(
              <button type="button" className="btn btn-primary" onClick={() => void refetch()} disabled={abfrage.isFetching}>
                <RefreshCw size={16} aria-hidden="true" className={abfrage.isFetching ? 'spin' : undefined} /> Nochmal versuchen
              </button>
            )}
          />
        </Rahmen>
      );
    }
    return (
      <Rahmen onZurueck={onZurueck} laedt>
        <div className="fbm-laden" aria-busy="true" aria-live="polite">
          <div className="fbm-skelett fbm-skelett--zeile" />
          <div className="fbm-skelett fbm-skelett--buehne" />
          <p><span className="loading-spinner" aria-hidden="true" /> Die Mail wird geladen …</p>
        </div>
      </Rahmen>
    );
  }

  const vorname = anredeName(daten.person.name);
  const anfrage = daten.anfrage;
  const antwort = mail.meine_anfrage?.antwort ?? anfrage?.antwort ?? null;
  const versendet = mail.status === 'sent';
  const bisTag = alsTag(mail.feedback_bis);
  const bisVorbei = !!bisTag && bisTag < alsTag(new Date())!;
  const fragender = anfrage?.angefragt_von_name?.trim() || 'Das Team';

  // Ohne passende Anmeldung nur lesen — der Gruß lädt dann nicht zum Kommentieren ein.
  const gruss = versendet
    ? darfSchreiben
      ? 'diese Mail ist schon verschickt. Du kannst sie Dir ansehen — Hinweise helfen uns beim nächsten Mal.'
      : 'diese Mail ist schon verschickt. Du kannst sie Dir hier ansehen.'
    : !darfSchreiben
      ? `${anfrage ? 'wir würden gern wissen, was Du zu dieser Mail sagst. Ansehen kannst Du sie hier' : 'hier kannst Du Dir die Mail ansehen'}${
        admin || anmeldung?.status === 'keine_adresse' ? '.' : ' — zum Kommentieren meldest Du Dich direkt darunter an.'}`
      : antwort === 'passt'
        ? 'Du hast schon „Passt so“ gegeben — danke! Falls Dir noch etwas auffällt, kannst Du gern weiter kommentieren.'
        : antwort === 'kommentiert'
          ? 'danke für Deine Rückmeldung! Schau gern noch einmal drüber, ob alles passt.'
          : anfrage
            ? 'wir würden gern wissen, was Du zu dieser Mail sagst.'
            : 'hier kannst Du Dir die Mail ansehen und Rückmeldung geben.';

  return (
    <Rahmen
      onZurueck={onZurueck}
      kopf={(
        <>
          <p className="fbm-gruss">Hallo {vorname}, {gruss}</p>
          <h1 className="fbm-titel">{betreff || 'Ohne Betreff'}</h1>
        </>
      )}
    >
      {anmeldung && (admin
        ? <AdminAnsichtBanner anmeldung={anmeldung} name={personName} mailId={mail.id} />
        : <AnmeldeBanner anmeldung={anmeldung} />)}

      <section className="fbm-info" aria-label="Zur Mail">
        <div className="fbm-chips">
          <StandBadge stand={mail.stand} />
          <VersandChip mail={mail} />
          <EventChip mail={mail} />
          <EmpfaengerChip mail={mail} />
        </div>

        {(anfrage?.nachricht || mail.feedback_bis) && !versendet && (
          <div className="fbm-anfrage">
            {anfrage?.nachricht && (
              <figure className="fbm-bitte">
                <Avatar name={fragender} groesse="md" />
                <div>
                  <figcaption><strong>{fragender}</strong> fragt:</figcaption>
                  <blockquote className="fbm-bitte-text">{anfrage.nachricht}</blockquote>
                </div>
              </figure>
            )}
            {mail.feedback_bis && (
              <p className={`fbm-frist${bisVorbei ? ' fbm-frist--vorbei' : ''}`}>
                <Hourglass size={15} aria-hidden="true" />
                <span>
                  {bisVorbei
                    ? <>Rückmeldung war bis <strong>{datumKurz(mail.feedback_bis)}</strong> erbeten — sie ist trotzdem willkommen.</>
                    : <>Rückmeldung bitte bis <strong>{datumKurz(mail.feedback_bis)}</strong> ({tageText(mail.feedback_bis)}).</>}
                </span>
              </p>
            )}
          </div>
        )}

      </section>

      {!erklaert && !versendet && darfSchreiben && (
        <section className="fbm-erklaerung" aria-labelledby="fbm-erklaerung-titel">
          <div className="fbm-erklaerung-kopf">
            <h2 id="fbm-erklaerung-titel">So gibst Du Rückmeldung</h2>
            <button type="button" className="fbm-schliessen" onClick={erklaerungSchliessen} aria-label="Erklärung ausblenden">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <ul className="fbm-schritte">
            <Schritt icon={MessageSquareText} titel="Kommentieren" text="Text in der Mail markieren und dazu schreiben — oder allgemein rechts in der Spalte." />
            <Schritt icon={PenTool} titel="Mit dem Stift markieren" text="Eine Stelle einkreisen oder anstreichen, zum Beispiel ein Bild, und kurz sagen, was gemeint ist." />
            <Schritt icon={PenLine} titel="Text ändern" text="Direkt in der Mail umschreiben. Das wird ein Vorschlag — die Mail ändert sich erst, wenn das Team zustimmt." />
            <Schritt icon={Trash2} titel="Entfernen" text="Soll ein ganzer Absatz oder Kasten weg? Tipp ihn an — er verschwindet samt Hintergrund. Auch das wird ein Vorschlag, das Team entscheidet." />
            <Schritt icon={CheckCircle2} titel="Passt so" text="Alles in Ordnung? Ein Klick genügt, dann weiß das Team Bescheid." />
          </ul>
          <button type="button" className="btn btn-secondary btn-sm fbm-verstanden" onClick={erklaerungSchliessen}>Verstanden</button>
        </section>
      )}

      <div className="fbm-werkstatt">
        <MailWerkstatt key={mail.id} mail={mail} adapter={adapter} />
      </div>
    </Rahmen>
  );
}

function Schritt({ icon: Icon, titel, text }: { icon: LucideIcon; titel: string; text: string }) {
  return (
    <li className="fbm-schritt">
      <span className="fbm-schritt-symbol" aria-hidden="true"><Icon size={17} /></span>
      <span className="fbm-schritt-text"><strong>{titel}</strong><span>{text}</span></span>
    </li>
  );
}

/** Seite mit Markenkopf, Inhalt und Fuß. */
function Rahmen({ onZurueck, kopf, laedt = false, children }: {
  onZurueck: () => void; kopf?: React.ReactNode; laedt?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="fbm-seite">
      <header className="fbm-kopf">
        <div className="fbm-kopf-innen">
          <div className="fbm-kopf-zeile">
            <button type="button" className="fbm-zurueck" onClick={onZurueck}>
              <ArrowLeft size={16} aria-hidden="true" /> Alle Mails
            </button>
            <span className="fbm-marke"><Mail size={13} aria-hidden="true" /> Randori Mail · Abstimmung</span>
          </div>
          {kopf ?? (laedt ? <div className="fbm-kopf-platzhalter" aria-hidden="true"><span /><span /></div> : null)}
        </div>
      </header>
      <main className="fbm-haupt">{children}</main>
      <footer className="fbm-fuss">
        <p><Lock size={12} aria-hidden="true" /> Dieser Link ist persönlich — bitte nicht weitergeben.</p>
        <p>© {new Date().getFullYear()} Randori-Pro Management GmbH</p>
      </footer>
    </div>
  );
}

function Hinweis({ icon: Icon, titel, text, onZurueck, extra }: {
  icon: LucideIcon; titel: string; text: string; onZurueck: () => void; extra?: React.ReactNode;
}) {
  return (
    <div className="fbm-hinweis" role="alert">
      <span className="fbm-hinweis-symbol" aria-hidden="true"><Icon size={26} /></span>
      <h1>{titel}</h1>
      <p>{text}</p>
      <div className="fbm-hinweis-knoepfe">
        {extra}
        <button type="button" className={`btn ${extra ? 'btn-secondary' : 'btn-primary'}`} onClick={onZurueck}>
          <ArrowLeft size={16} aria-hidden="true" /> Alle Mails
        </button>
      </div>
    </div>
  );
}
